import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APP_LABELS, APPS, DOMAIN_PURPOSE_LABELS, DOMAIN_PURPOSES, type AppName, type DomainPurpose, type DomainStatus, type DomainView, type ReleaseView } from '@lp/contracts';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { del, errorText, fieldProblems, get, patch, post } from '@/lib/api';
import { useIsSuperAdmin } from '@/lib/auth';
import { dateTime } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { Badge, Button, Card, Dialog, ErrorBox, Field, Input, Loading, PageHeader, Select } from '@/components/ui';

const STATUS_TONE: Record<DomainStatus, 'green' | 'blue' | 'amber' | 'gray'> = { active: 'green', verified: 'blue', pending: 'amber', disabled: 'gray' };
const STATUS_LABEL: Record<DomainStatus, string> = { active: 'Active', verified: 'Proven', pending: 'Waiting for DNS', disabled: 'Disabled' };

/** a table of domains, with prove / activate / disable for the Super Admin */
export function DomainTable({ domains, showClient, onChange }: { domains: DomainView[]; showClient: boolean; onChange: () => void }) {
  const t = useT();
  const superAdmin = useIsSuperAdmin();
  const verify = useMutation({ mutationFn: (id: number) => post<DomainView>(`/platform/domains/${id}/verify`), onSuccess: onChange });
  const status = useMutation({ mutationFn: (x: { id: number; status: 'active' | 'disabled' }) => patch<DomainView>(`/platform/domains/${x.id}`, { status: x.status }), onSuccess: onChange });
  const purpose = useMutation({ mutationFn: (x: { id: number; purpose: DomainPurpose }) => patch<DomainView>(`/platform/domains/${x.id}`, { purpose: x.purpose }), onSuccess: onChange });
  const remove = useMutation({ mutationFn: (id: number) => del(`/platform/domains/${id}`), onSuccess: onChange });
  if (!domains.length) return <p className="text-sm text-ink-600">{t('No domain yet.')}</p>;
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <tbody>
            {domains.map((d) => (
              <tr key={d.id} className="border-b border-ink-100 align-top last:border-0" data-testid={`domain-${d.host}`}>
                {showClient && (
                  <td className="py-2 pr-3">
                    <Link to={`/clients/${d.clientId}`} className="font-mono text-xs hover:underline">
                      {d.clientCode}
                    </Link>
                  </td>
                )}
                <td className="py-2 pr-3">
                  <span className="font-mono text-xs">{d.host}</span>
                  <span className="flex flex-wrap items-center gap-1 text-[11px] text-ink-500">
                    {t(d.kind === 'subdomain' ? 'Automatic address' : 'Own domain')} ·
                    {superAdmin ? (
                      <Select value={d.purpose} onChange={(e) => purpose.mutate({ id: d.id, purpose: e.target.value as DomainPurpose })} className="h-6 px-1 text-[11px]" aria-label={`${t('Serves')} ${d.host}`}>
                        {DOMAIN_PURPOSES.map((p) => (
                          <option key={p} value={p}>
                            {t(DOMAIN_PURPOSE_LABELS[p])}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      t(DOMAIN_PURPOSE_LABELS[d.purpose])
                    )}
                  </span>
                  {d.verification && d.status === 'pending' && (
                    <span className="mt-1 block rounded bg-ink-50 px-2 py-1 text-[11px]">
                      {t('Add this DNS TXT record')}: <span className="font-mono">{d.verification.record}</span> = <span className="font-mono">{d.verification.value}</span>
                    </span>
                  )}
                  {d.lastCheckProblem && <span className="block text-[11px] text-red-700">{d.lastCheckProblem}</span>}
                </td>
                <td className="py-2 pr-3">
                  <Badge tone={STATUS_TONE[d.status]}>{t(STATUS_LABEL[d.status])}</Badge>
                  {d.lastCheckedAt && <span className="block text-[11px] text-ink-500">{dateTime(d.lastCheckedAt)}</span>}
                </td>
                <td className="py-2 text-right">
                  {superAdmin && (
                    <span className="inline-flex gap-1">
                      {d.kind === 'custom' && d.status !== 'disabled' && (
                        <Button variant="ghost" onClick={() => verify.mutate(d.id)} disabled={verify.isPending}>
                          {t('Check DNS')}
                        </Button>
                      )}
                      {d.status !== 'active' && (d.kind === 'subdomain' || !!d.verifiedAt) && (
                        <Button variant="ghost" onClick={() => status.mutate({ id: d.id, status: 'active' })} disabled={status.isPending}>
                          {t('Activate')}
                        </Button>
                      )}
                      {d.status !== 'disabled' && (
                        <Button variant="ghost" onClick={() => status.mutate({ id: d.id, status: 'disabled' })} disabled={status.isPending}>
                          {t('Disable')}
                        </Button>
                      )}
                      {d.kind === 'custom' && d.status !== 'active' && (
                        <Button variant="ghost" onClick={() => window.confirm(`${t('Delete')} ${d.host}?`) && remove.mutate(d.id)} disabled={remove.isPending} className="text-red-700">
                          {t('Delete')}
                        </Button>
                      )}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(verify.error || status.error || purpose.error || remove.error) && <ErrorBox text={errorText(verify.error ?? status.error ?? purpose.error ?? remove.error)} />}
    </>
  );
}

/** a client's domains, on its page — with "add its own domain" */
export function ClientDomains({ clientId }: { clientId: number }) {
  const t = useT();
  const qc = useQueryClient();
  const superAdmin = useIsSuperAdmin();
  const list = useQuery({ queryKey: ['client', clientId, 'domains'], queryFn: () => get<DomainView[]>(`/platform/clients/${clientId}/domains`) });
  const [host, setHost] = useState('');
  const [purpose, setPurpose] = useState<DomainPurpose>('console');
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['client', clientId, 'domains'] });
    void qc.invalidateQueries({ queryKey: ['domains'] });
    void qc.invalidateQueries({ queryKey: ['client', clientId, 'entitlements'] });
  };
  const add = useMutation({ mutationFn: () => post<DomainView>(`/platform/clients/${clientId}/domains`, { host, purpose }), onSuccess: () => { setHost(''); refresh(); } });
  return (
    <Card title={t('Domains')} className="mt-4">
      {list.isLoading ? <Loading /> : list.error ? <ErrorBox text={errorText(list.error)} /> : <DomainTable domains={list.data ?? []} showClient={false} onChange={refresh} />}
      {superAdmin && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-ink-100 pt-3">
          <Input value={host} onChange={(e) => setHost(e.target.value.toLowerCase())} placeholder="lottery.example.com" className="w-60" aria-label={t('Own domain')} />
          <Select value={purpose} onChange={(e) => setPurpose(e.target.value as DomainPurpose)} aria-label={t('Serves')}>
            {DOMAIN_PURPOSES.map((p) => (
              <option key={p} value={p}>
                {t(DOMAIN_PURPOSE_LABELS[p])}
              </option>
            ))}
          </Select>
          <Button icon={<Plus size={14} />} onClick={() => add.mutate()} disabled={!host || add.isPending}>
            {t('Add own domain')}
          </Button>
          {add.error && <ErrorBox text={[errorText(add.error), ...fieldProblems(add.error)].join(' — ')} />}
        </div>
      )}
    </Card>
  );
}

/** every company's domains (master prompt §17): data, never code */
export function DomainsPage() {
  const t = useT();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['domains'], queryFn: () => get<DomainView[]>('/platform/domains') });
  return (
    <>
      <PageHeader title={t('Domains')} subtitle={t('Every company’s addresses. A company gets its automatic address when it is created; its own domain is proven by a DNS record, then activated here — no code change.')} />
      <Card>{list.isLoading ? <Loading /> : list.error ? <ErrorBox text={errorText(list.error)} /> : <DomainTable domains={list.data ?? []} showClient onChange={() => void qc.invalidateQueries({ queryKey: ['domains'] })} />}</Card>
    </>
  );
}

const RELEASE_TONE = { draft: 'blue', released: 'green', withdrawn: 'gray' } as const;

/** app releases (master prompt §18): built once, released to whom it targets; devices ask whether they must update */
export function ReleasesPage() {
  const t = useT();
  const qc = useQueryClient();
  const superAdmin = useIsSuperAdmin();
  const list = useQuery({ queryKey: ['releases'], queryFn: () => get<ReleaseView[]>('/platform/releases') });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ReleaseView | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['releases'] });
  const act = useMutation({ mutationFn: (x: { id: number; to: 'publish' | 'withdraw' }) => post<ReleaseView>(`/platform/releases/${x.id}/${x.to}`), onSuccess: refresh });
  return (
    <>
      <PageHeader
        title={t('Releases')}
        subtitle={t('Build once, release to everyone, to some packages or to some companies. Devices ask whether they must update — nothing is sent to a client by hand.')}
        actions={
          superAdmin && (
            <Button icon={<Plus size={14} />} onClick={() => setCreating(true)}>
              {t('New release')}
            </Button>
          )
        }
      />
      <Card>
        {list.isLoading ? (
          <Loading />
        ) : !list.data?.length ? (
          <p className="text-sm text-ink-600">{t('No release yet.')}</p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {list.data.map((r) => (
                <tr key={r.id} className="border-b border-ink-100 align-top last:border-0" data-testid={`release-${r.app}-${r.version}`}>
                  <td className="py-2 pr-3">
                    <span className="font-medium">{t(APP_LABELS[r.app])}</span> <span className="font-mono text-xs">{r.version}</span>
                    <span className="block text-[11px] text-ink-500">
                      {r.minimumVersion && `${t('Minimum')} ${r.minimumVersion} · `}
                      {r.mandatory && `${t('Mandatory')} · `}
                      {r.target.kind === 'all' ? t('Everyone') : `${t(r.target.kind === 'packages' ? 'Packages' : 'Clients')}: ${r.target.codes.join(', ')}`}
                    </span>
                    {r.notesEn && <span className="block text-xs text-ink-600">{r.notesEn}</span>}
                  </td>
                  <td className="py-2 pr-3">
                    <Badge tone={RELEASE_TONE[r.status]}>{t(r.status === 'draft' ? 'Draft' : r.status === 'released' ? 'Released' : 'Withdrawn')}</Badge>
                    {r.status === 'released' && (
                      <span className="block text-[11px] text-ink-500">
                        {r.reaches} {t('companies')}
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {superAdmin && r.status !== 'withdrawn' && (
                      <Button variant="ghost" onClick={() => setEditing(r)}>
                        {t('Edit')}
                      </Button>
                    )}
                    {superAdmin && r.status === 'draft' && (
                      <Button variant="secondary" onClick={() => act.mutate({ id: r.id, to: 'publish' })} disabled={act.isPending}>
                        {t('Release')}
                      </Button>
                    )}
                    {superAdmin && r.status === 'released' && (
                      <Button variant="ghost" onClick={() => act.mutate({ id: r.id, to: 'withdraw' })} disabled={act.isPending}>
                        {t('Withdraw')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {act.error && <ErrorBox text={errorText(act.error)} />}
      </Card>
      {creating && <NewRelease onClose={() => setCreating(false)} onMade={() => { setCreating(false); refresh(); }} />}
      {editing && <NewRelease editing={editing} onClose={() => setEditing(null)} onMade={() => { setEditing(null); refresh(); }} />}
    </>
  );
}

/** a new release — or (editing) a draft changed freely, a released one in its notes, link and who gets it */
function NewRelease({ onClose, onMade, editing }: { onClose: () => void; onMade: () => void; editing?: ReleaseView }) {
  const t = useT();
  const [f, setF] = useState(
    editing
      ? { app: editing.app, version: editing.version, minimumVersion: editing.minimumVersion ?? '', mandatory: editing.mandatory, notesEn: editing.notesEn, notesKm: editing.notesKm, url: editing.url ?? '', target: editing.target.kind, codes: editing.target.kind === 'all' ? '' : editing.target.codes.join(', ') }
      : { app: 'agent_apk' as AppName, version: '', minimumVersion: '', mandatory: false, notesEn: '', notesKm: '', url: '', target: 'all' as 'all' | 'packages' | 'clients', codes: '' },
  );
  const released = editing?.status === 'released';
  const target = f.target === 'all' ? { kind: 'all' } : { kind: f.target, codes: f.codes.split(/[\s,]+/).filter(Boolean) };
  const create = useMutation({
    mutationFn: () =>
      editing
        ? patch<ReleaseView>(`/platform/releases/${editing.id}`, { notesEn: f.notesEn, notesKm: f.notesKm, url: f.url || null, target, ...(released ? {} : { minimumVersion: f.minimumVersion || null, mandatory: f.mandatory }) })
        : post<ReleaseView>('/platform/releases', { app: f.app, version: f.version, minimumVersion: f.minimumVersion || null, mandatory: f.mandatory, notesEn: f.notesEn, notesKm: f.notesKm, url: f.url || null, target }),
    onSuccess: onMade,
  });
  return (
    <Dialog
      title={editing ? `${t('Edit')} — ${t(APP_LABELS[editing.app])} ${editing.version}` : t('New release')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => create.mutate()} disabled={!f.version || create.isPending}>
            {editing ? t('Save') : t('Create draft')}
          </Button>
        </>
      }
    >
      <Field label={t('App')}>
        <Select value={f.app} onChange={(e) => setF({ ...f, app: e.target.value as AppName })} className="w-full" disabled={!!editing}>
          {APPS.map((a) => (
            <option key={a} value={a}>
              {t(APP_LABELS[a])}
            </option>
          ))}
        </Select>
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label={t('Version')}>
          <Input value={f.version} onChange={(e) => setF({ ...f, version: e.target.value })} placeholder="1.2.42" disabled={!!editing} />
        </Field>
        <Field label={t('Minimum')}>
          <Input value={f.minimumVersion} onChange={(e) => setF({ ...f, minimumVersion: e.target.value })} placeholder="1.2.40" disabled={released} />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={f.mandatory} onChange={(e) => setF({ ...f, mandatory: e.target.checked })} disabled={released} />
        {t('Mandatory — devices must update before they go on')}
      </label>
      <Field label={t('Link to the build (APK)')}>
        <Input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://…/agent-1.2.42.apk" />
      </Field>
      <Field label={t('Notes (English)')}>
        <Input value={f.notesEn} onChange={(e) => setF({ ...f, notesEn: e.target.value })} />
      </Field>
      <Field label={t('Notes (Khmer)')}>
        <Input value={f.notesKm} onChange={(e) => setF({ ...f, notesKm: e.target.value })} />
      </Field>
      <Field label={t('Who gets it')}>
        <Select value={f.target} onChange={(e) => setF({ ...f, target: e.target.value as typeof f.target })} className="w-full">
          <option value="all">{t('Everyone')}</option>
          <option value="packages">{t('The companies of these packages')}</option>
          <option value="clients">{t('These companies')}</option>
        </Select>
      </Field>
      {f.target !== 'all' && <Input value={f.codes} onChange={(e) => setF({ ...f, codes: e.target.value.toUpperCase() })} placeholder={f.target === 'packages' ? 'FULL, STANDARD' : 'DEMOA, GOLD'} />}
      {create.error && <ErrorBox text={[errorText(create.error), ...fieldProblems(create.error)].join(' — ')} />}
    </Dialog>
  );
}
